import React, { PropTypes } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { ButtonToolbar } from "react-bootstrap";
import { FormattedDate, FormattedMessage, FormattedTime } from "react-intl";
import { SubmissionError } from "redux-form";
import { getTenantFromURL } from "../../../../common/MultiTenancy/MultiTenancyHelper";

import {
  ArchiveButton,
  DownloadS3Button,
  EditButton,
  Render,
} from "../../../../common";
import { DataTable } from "../../../../common/DataTable";
import { BasicClientAttachmentFields, ClientAttachmentStatusToggler } from "./";

import { CLIENT_ATTACHMENTS } from "../../../../../utils/data/models";
import * as dataActions from "../../../../../utils/data/dataActions";
import * as types from "../../../../../utils/data/dataActionTypes";

import {
  ADMINISTRATOR,
  AGENT,
  DIRECTOR,
  FINANCE,
  FINTECH,
  INFORMATION_AGENT,
  MANAGER,
  PLAN_ADMINISTRATOR,
} from "../../../../../utils/Roles";
import userHasAccess from "../../../../../utils/userHasAccess";
import englishTranslations from "../../../../../components/intl/en-CA";
import frenchTranslations from "../../../../../components/intl/fr-CA";
import { ACTIVE, IMPLEMENTATION, INACTIVE, TERMINATED } from "../../enums/clientStatusTypes";

class ClientAttachmentsDataTable extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.createClientAttachment = this.createClientAttachment.bind(this);
    this.createEmptyClientAttachment = this.createEmptyClientAttachment.bind(
      this
    );
    this.deleteClientAttachment = this.deleteClientAttachment.bind(this);
    this.getData = this.getData.bind(this);
    this.onDownloaded = this.onDownloaded.bind(this);
    this.buildDescription = this.buildDescription.bind(this);
    this.getCanArchiveOrAddAttachmentForClientStatusType = this.getCanArchiveOrAddAttachmentForClientStatusType.bind(this);

    this.canArchiveOrAddAttachmentForImplementation = userHasAccess([ADMINISTRATOR, FINTECH, PLAN_ADMINISTRATOR, DIRECTOR, FINANCE], true);
    this.canArchiveOrAddAttachmentForActive = userHasAccess([ADMINISTRATOR, FINTECH, DIRECTOR, MANAGER, PLAN_ADMINISTRATOR, INFORMATION_AGENT], true);
    this.canArchiveOrAddAttachmentForTermination = userHasAccess(FINTECH);
  }

  componentWillMount() {
    this.props.dataActions.clearDataList(CLIENT_ATTACHMENTS);
  }

  getCanArchiveOrAddAttachmentForClientStatusType(clientStatusType) {
    switch(clientStatusType) {
      case IMPLEMENTATION.value:
        return this.canArchiveOrAddAttachmentForImplementation;
      case ACTIVE.value:
        case INACTIVE.value:
          return this.canArchiveOrAddAttachmentForActive;
          case TERMINATED.value:
            return this.canArchiveOrAddAttachmentForTermination;
    }
  }

  createClientAttachment(values) {
    if (values.clientAttachments && values.clientAttachments.length) {
      values.clientAttachments.forEach((clientAttachment) => {
        clientAttachment.objectPath.startsWith(`${getTenantFromURL()}/`) ?  
        clientAttachment.objectPath = clientAttachment.objectPath.replace(`${getTenantFromURL()}/`,'') : 
        clientAttachment.objectPath; });
      return this.props.dataActions
        .createDataItem(CLIENT_ATTACHMENTS, values.clientAttachments)
        .then((data) => {
          this.getData(
            this.props.offset,
            this.props.limit,
            this.props.q,
            this.props.sortBy,
            this.props.filters,
            data.type
          );
        });
    } else {
      throw new SubmissionError({
        clientAttachmentUploader: {
          messageKey: "validation.isRequiredFile",
          values: {},
          state: "error",
        },
      });
    }
  }

  createEmptyClientAttachment() {
    const { clientGuid, clientId } = this.props;
    return {
      clientId,
      clientGuid,
    };
  }

  deleteClientAttachment(clientAttachmentId) {
    this.props.dataActions
      .deleteDataItem(CLIENT_ATTACHMENTS, clientAttachmentId)
      .then(() => {
        this.getData(
          this.props.offset,
          this.props.limit,
          this.props.q,
          this.props.sortBy,
          this.props.filters
        );
      });
  }

  getCols() {
    const { activeClient } = this.props;
    const canUseStatusToggler = this.getCanArchiveOrAddAttachmentForClientStatusType(activeClient.clientStatusType);
    return [
      {
        name: "",
        display: (clientAttachment) => {
          const { offset, limit, q, sortBy, filters } = this.props;
          return (
            <ClientAttachmentStatusToggler
              attachment={clientAttachment}
              disabled={!canUseStatusToggler}
              afterToggle={this.getData.bind(
                this,
                offset,
                limit,
                q,
                sortBy,
                filters
              )}
            />
          );
        },
        widthPct: 3,
      },
      {
        name: (
          <FormattedMessage id="clientAttachments.datatable.columns.createdAt" />
        ),
        key: "createdAt",
        display: (clientAttachment) => (
          <span className="highlighted-datetime">
            <span className="date">
              <FormattedDate
                value={clientAttachment.createdAt}
                year="numeric"
                month="long"
                day="2-digit"
              />
            </span>
            <span className="time">
              <FormattedTime
                value={clientAttachment.createdAt}
                hour="numeric"
                minute="numeric"
              />
            </span>
          </span>
        ),
        widthPct: 16,
      },
      {
        name: (
          <FormattedMessage id="clientAttachments.datatable.columns.createdBy" />
        ),
        sortKeys: ["createdByUser.firstName", "createdByUser.lastName"],
        display: (clientAttachment) => (
          <span>{`${clientAttachment.createdByUser.firstName} ${clientAttachment.createdByUser.lastName}`}</span>
        ),
        widthPct: 25,
      },
      {
        name: (
          <FormattedMessage id="clientAttachments.datatable.columns.attachmentName" />
        ),
        key: "objectName",
        display: (clientAttachment) => (
          <span>{clientAttachment.objectName}</span>
        ),
        widthPct: 25,
      },
      {
        name: <FormattedMessage id="datatable.actions" />,
        display: (clientAttachment) =>
          this.generateActionButtons(clientAttachment),
        widthPct: 31,
      },
    ];
  }

  buildDescription(attachment){
    let createdAtDate = attachment.createdAt.split("T")[0];
    let fileName = attachment.objectName;

    let clientName = this.props.currentClientName;

    const userLanguage = localStorage.getItem("languageCode") || "en-CA";
    if(userLanguage == "fr-CA"){
      return frenchTranslations.downloader.clientAttachment + ": " + clientName + " " + createdAtDate + " " + fileName;
    }

    return englishTranslations.downloader.clientAttachment + ": " + clientName + " " + createdAtDate + " - " + fileName;
  }

  generateActionButtons(clientAttachment) {
    const createdByUser = clientAttachment.createdByUser.userId;
    const user = this.props.auth.user.userId;

    const { activeClient } = this.props;
    const canArchiveFile = this.getCanArchiveOrAddAttachmentForClientStatusType(activeClient.clientStatusType);

    return (
      <ButtonToolbar key={clientAttachment.clientAttachmentId}>
        <DownloadS3Button
          model={CLIENT_ATTACHMENTS}
          attachment={clientAttachment}
          identifier={this.buildDescription(clientAttachment)}
          label={<FormattedMessage id="common.download" />}
          onDownloaded={this.onDownloaded}
        />

        {(createdByUser == user || userHasAccess(DIRECTOR)) && (
          <Render for={AGENT} exactly={[PLAN_ADMINISTRATOR, INFORMATION_AGENT]}>
            <ArchiveButton
              archivedName={clientAttachment.objectName}
              archiveFunction={() => {
                this.deleteClientAttachment(
                  clientAttachment.clientAttachmentId
                );
              }}
              label={<FormattedMessage id="common.delete" />}
              title={"archiveButton.modal.title.delete"}
              confirmationMessage="archiveButton.confirmationMessage.delete"
              disabled={!canArchiveFile}
            />
          </Render>
        )}
      </ButtonToolbar>
    );
  }

  generateAddButton() {
    return (
      <div>
        <Render for={AGENT} exactly={[PLAN_ADMINISTRATOR, INFORMATION_AGENT]}>
          <EditButton
            modalClassName="modal-attachments"
            title={
              <FormattedMessage id="clientAttachments.datatable.actions.add" />
            }
            label={<FormattedMessage id="common.add" />}
            add={true}
            form={BasicClientAttachmentFields}
            data={this.createEmptyClientAttachment()}
            editFunction={this.createClientAttachment}
            confirmationTitle={
              <FormattedMessage id="admin.blocs.datatable.actions.showConfirm.title">
                {(f) => <span className="icon warning">{f}</span>}
              </FormattedMessage>
            }
            confirmationMessage={
              <FormattedMessage id="admin.blocs.datatable.actions.showConfirm.message" />
            }
            requiresCloseConfirmation={true}
            confirmOnClose={true}
          />
        </Render>
      </div>
    );
  }

  getData(offset, limit, q, sortBy, filters, actionType = "") {
    switch (actionType) {
      case types.CREATE_DATA_ITEM_FAILURE: {
        throw new SubmissionError({
          clientAttachmentUploader: {
            messageKey: "validation.isRequiredFile",
            values: {},
            state: "error",
          },
        });
      }
      default: {
        const userId = this.props.auth.user.userId;
        return this.props.dataActions
          .getDataList(
            CLIENT_ATTACHMENTS,
            offset,
            limit,
            q,
            sortBy,
            filters,
            null,
            { clientId: this.props.clientId }
          )
          .then(() => {
            this.props.dataActions.getDataCount(CLIENT_ATTACHMENTS, [
              `clientAttachments.clientId==${this.props.clientId};clientAttachmentsUnread.userId==${userId}`,
            ]);
          });
      }
    }
  }

  markAsRead(attachmentId, unread) {
    if (!unread) {
      return;
    }
    const model = { path: `${CLIENT_ATTACHMENTS.path}/${attachmentId}/read` };

    return this.props.dataActions.simplePost({ model }).then(() => {
      this.getData(
        this.props.offset,
        this.props.limit,
        this.props.q,
        this.props.sortBy,
        this.props.filters
      );
    });
  }

  onDownloaded(clientAttachment) {
    
    this.markAsRead(
      clientAttachment.clientAttachmentId,
      clientAttachment.unread
    );
    this.props.dataActions.getDataItem(
      CLIENT_ATTACHMENTS,
      clientAttachment.clientAttachmentId
    );
  }

  render() {
    const { activeClient } = this.props;
    const canAddClientAttachment = this.getCanArchiveOrAddAttachmentForClientStatusType(activeClient.clientStatusType);
    return (
      <DataTable
        ref="dataTable"
        title={<FormattedMessage id="clientAttachments.title" />}
        getData={this.getData}
        cols={this.getCols()}
        q={this.props.q}
        filters={this.props.filters}
        offset={this.props.offset}
        data={this.props.items}
        rowsPerPage={this.props.limit}
        rowHeight={56}
        totalRows={this.props.total}
        showOverflow={false}
        sortBy={this.props.sortBy}
        addButton={canAddClientAttachment && this.generateAddButton()}
      />
    );
  }
}

ClientAttachmentsDataTable.propTypes = {
  attachment: PropTypes.object,
  auth: PropTypes.object,
  clientGuid: PropTypes.string,
  clientId: PropTypes.number,
  currentClientName: PropTypes.string,
  dataActions: PropTypes.object,
  filters: PropTypes.array,
  q: PropTypes.string,
  items: PropTypes.array,
  limit: PropTypes.number,
  offset: PropTypes.number,
  sortBy: PropTypes.array,
  total: PropTypes.number,
  activeClient: PropTypes.object
};

function mapStateToProps(state) {
  const data = state.data.clientAttachments || {};
  const newClient = data.clientId != state.data.clients.active.clientId;
  const currentClientName = state.data.clients.active.fullName;
  const pagination = data.pagination || {};
  const activeClient = state.data.clients.active;
  return {
    auth: state.auth || {},
    q: data.q,
    filters: newClient
      ? [`clientId==${state.data.clients.active.clientId}`]
      : data.filters,
    clientGuid: state.data.clients.active.clientGuid,
    clientId: state.data.clients.active.clientId,
    currentClientName: currentClientName,
    limit: pagination.limit || 100,
    offset: pagination.offset || 0,
    sortBy: data.sortBy || ["createdAt:desc"],
    total: pagination.totalCount || 0,
    items: data.items || null,
    activeClient
  };
}

function mapDispatchToProps(dispatch) {
  return {
    dataActions: bindActionCreators(dataActions, dispatch),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ClientAttachmentsDataTable);



// WEBPACK FOOTER //
// ./src/components/dashboard/clients/client/clientAttachments/ClientAttachmentsDataTable.js